(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) module.exports = api;
  if (!root || !root.document) return;

  const start = () => api.start(root.document);
  if (root.document.readyState === 'loading') {
    root.document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})(typeof window !== 'undefined' ? window : undefined, function () {
  const statusSelector = '[data-yfl-backorder-status]';
  const shippingSelector = '[data-yfl-standard-shipping]';

  function isBackorderedVariant(variant) {
    if (!variant) return false;
    const policy = variant.inventory_policy ?? variant.policy;
    const quantity = variant.inventory_quantity ?? variant.quantity;
    return policy === 'continue' && Number(quantity) <= 0;
  }

  function parseIsoDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const timestamp = Date.UTC(year, month - 1, day);
    const parsed = new Date(timestamp);
    if (
      parsed.getUTCFullYear() !== year ||
      parsed.getUTCMonth() !== month - 1 ||
      parsed.getUTCDate() !== day
    ) {
      return null;
    }
    return parsed;
  }

  function isCurrentDispatchDate(value, now = new Date()) {
    const dispatchDate = parseIsoDate(value);
    if (!dispatchDate) return false;
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return dispatchDate.getTime() >= today;
  }

  function formatDispatchDate(value, locale) {
    const dispatchDate = parseIsoDate(value);
    if (!dispatchDate) return value || '';

    try {
      return new Intl.DateTimeFormat(locale || 'en', {
        dateStyle: 'medium',
        timeZone: 'UTC',
      }).format(dispatchDate);
    } catch (_error) {
      return value;
    }
  }

  function resolveDispatchMessage(status, now = new Date(), locale = 'en') {
    const dispatchDate = status?.dispatchDate || '';
    const unknownMessage = status?.unknownMessage || '';
    const estimatedTemplate = status?.estimatedTemplate || '';
    if (!isCurrentDispatchDate(dispatchDate, now) || !estimatedTemplate.includes('[date]')) {
      return unknownMessage;
    }

    return estimatedTemplate.replace('[date]', formatDispatchDate(dispatchDate, locale));
  }

  function updateStandardShipping(document, sectionId, backordered) {
    if (!sectionId) return;
    document.querySelectorAll(shippingSelector).forEach((shipping) => {
      if (shipping.dataset.yflSection !== String(sectionId)) return;
      shipping.hidden = backordered;
    });
  }

  function updateStatus(status, variant, document) {
    const backordered = isBackorderedVariant(variant);
    status.hidden = !backordered;
    status.setAttribute('aria-hidden', backordered ? 'false' : 'true');

    if (backordered) {
      const message = status.querySelector('[data-yfl-backorder-message]');
      if (message) {
        message.textContent = resolveDispatchMessage(
          {
            dispatchDate: status.dataset.yflDispatchDate,
            estimatedTemplate: status.dataset.yflEstimatedTemplate,
            unknownMessage: status.dataset.yflUnknownMessage,
          },
          new Date(),
          document.documentElement.lang
        );
      }
    }

    updateStandardShipping(document, status.dataset.yflSection, backordered);
  }

  function initialiseStatus(status, document) {
    if (status.dataset.yflBackorderInitialised === 'true') return;
    status.dataset.yflBackorderInitialised = 'true';
    updateStatus(
      status,
      {
        policy: status.dataset.yflCurrentPolicy,
        quantity: status.dataset.yflCurrentQuantity,
      },
      document
    );
  }

  function initialiseWithin(node, document) {
    if (!(node instanceof Element)) return;
    if (node.matches(statusSelector)) initialiseStatus(node, document);
    node.querySelectorAll(statusSelector).forEach((status) => initialiseStatus(status, document));
  }

  function updateSection(document, sectionId, variant) {
    document.querySelectorAll(statusSelector).forEach((status) => {
      if (status.dataset.yflSection !== String(sectionId)) return;
      updateStatus(status, variant, document);
    });
  }

  function start(document) {
    document.querySelectorAll(statusSelector).forEach((status) => initialiseStatus(status, document));

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const data = event?.data;
        if (!data?.sectionId || !data.variant) return;
        updateSection(document, data.sectionId, data.variant);
      });
    }

    if (typeof MutationObserver === 'function') {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => initialiseWithin(node, document));
        });
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  return {
    formatDispatchDate,
    isBackorderedVariant,
    isCurrentDispatchDate,
    parseIsoDate,
    resolveDispatchMessage,
    start,
    updateSection,
  };
});
